import { trackEvent } from '../util/analytics'

// Audio player
// Use `render_hidden_audio` to place hidden <audio> elements inside of it.
// Controlled by start/stop buttons that need to be placed inside of it.
// Will play all audios inside of it, one after the other.
// Autoplays if audio has not been manually stopped with the stop button.
//
up.compiler('[read-aloud]', async (element, { autoPlay, saveSettingUrl, setPagePlayedUrl }) => {

  const TRACK_PAGE_READ_ALOUD_COUNTS = [2, 5]

  const startButtonSelector = '[read-aloud--start-button]'
  const stopButtonSelector = '[read-aloud--stop-button]'
  const audios = element.querySelectorAll('audio')
  const audiosByPlaybackIndex = Object.fromEntries([...audios].map(audio => [audio.dataset.playbackIndex, audio]))

  if (audios.length < 1) {
    console.warn('[read-aloud] No audio elements found.')
    return
  }

  let playedAnything = false
  let playing = false
  let allAudiosPlayed = false
  let userPressedStop = false
  let playCountAfterFirstStop = 0
  let playCountAfterFirstFinish = 0
  let currentAudio
  const audioPlayingClass = 'audio-playing'
  const highlightClass = 'highlight'

  function isPlayable(audio) {
    return up.element.isVisible(audio.parentElement)
  }

  function onAnyPlaybackEnded(event, playedElement) {
    const { playbackIndex } = playedElement.dataset

    if (playedElement === audios[audios.length - 1]) {
      playing = false
      allAudiosPlayed = true
      updateVisibleButtonText()
    }

    if (playbackIndex) {
      const nextIndex = `${parseInt(playbackIndex) + 1}`
      const nextAudio = audiosByPlaybackIndex[nextIndex]

      if (!nextAudio) return

      if (playing && isPlayable(nextAudio)) {
        playAudio(nextAudio)
      } else {
        // Next element in line cannot be played because its container is hidden.
        // We skip over it so the potentially next (or later) element can play.
        up.emit(nextAudio, 'app:playback:ended')
      }
    }
  }

  function onPlaybackContentRevealed({ elements }) {
    if (!playedAnything) return

    const revealedAudio = [...audios].find(audio => [...elements].some(el => el.contains(audio)))

    if (revealedAudio && isPlayable(revealedAudio)) {
      playAudio(revealedAudio, { throwError: true })
        .catch(up.util.noop) // Ignore play errors. Those happen when reloading a solved slide and can be ignored.
    }
  }

  async function onClickButton() {
    const requestedPlayback = !playing

    if (requestedPlayback) {
      await play()
      trackEvent('read-aloud', 'click-play')
      if (allAudiosPlayed && playCountAfterFirstFinish === 1) {
        trackEvent('read-aloud', 'play-again-after-reading-finished')
      } else if (userPressedStop && playCountAfterFirstStop === 1) {
        trackEvent('read-aloud', 'play-again-after-stopping')
      }
    } else if (currentAudio) {
      stopAudio(currentAudio)
      trackEvent('read-aloud', 'click-stop')
      playedAnything = false // clicking stop reverts to "not played anything yet"
      playing = false
      userPressedStop = true
      updateVisibleButtonText()
    }

    saveAutoPlaySetting(requestedPlayback)
  }

  async function onAudioEnded(audio) {
    const waitMs = audio.dataset.waitAfter
    if (waitMs) {
      await new Promise((resolve, reject) => {
        up.util.timer(waitMs, resolve)
      })
    }
    stopAudio(audio)

    up.emit(audio, 'app:playback:ended')
  }

  function updateVisibleButtonText() {
    element.querySelectorAll(startButtonSelector).forEach((button) => { button.hidden = playing })
    element.querySelectorAll(stopButtonSelector).forEach((button) => { button.hidden = !playing })
  }

  async function play({ throwError = false } = {}) {
    if (!isPlayable(audios[0])) return

    trackPlayCountAfterFirstStop()
    trackPlayCountAfterFirstFinish()

    const played = await playAudio(audios[0], { throwError })

    if (played && setPagePlayedUrl) {
      up.request(setPagePlayedUrl, { method: 'PATCH' })
        .then(afterSetPagePlayed)
    }
  }

  async function playAudio(audio, { throwError = false } = {}) {
    if (currentAudio) {
      // avoid playing multiple audios at the same time
      stopAudio(currentAudio)
    }

    if (!audio.isConnected) {
      return
    }

    audio.currentTime = 0

    try {
      await audio.play()
      playing = true
      playedAnything = true

    } catch (error) {
      playing = false

      if (throwError) {
        throw error
      } else {
        console.error(error.message)
        showPlayErrorDialog()
        trackEvent('read-aloud', 'play-error', error.message)
      }
    }

    setPlaying(audio, true)
    currentAudio = audio

    updateVisibleButtonText()

    return playing
  }

  function stopAudio(audio) {
    audio.pause()
    audio.currentTime = 0
    setPlaying(audio, false)
    currentAudio = undefined
  }

  function setPlaying(audio, isPlaying) {
    audio.classList.toggle(audioPlayingClass, isPlaying)

    let container = audio.closest('[read-aloud--highlight]')
    container ??= element.querySelector(`#${audio.getAttribute('for')}`)
    if (container) container.classList.toggle(highlightClass, isPlaying)
  }

  function saveAutoPlaySetting(autoPlayEnabled) {
    up.request(saveSettingUrl, { method: 'PATCH', params: { read_aloud: autoPlayEnabled } })
  }

  function trackPlayCountAfterFirstStop() {
    if (userPressedStop) {
      playCountAfterFirstStop++
    }
  }

  function trackPlayCountAfterFirstFinish() {
    if (allAudiosPlayed) {
      playCountAfterFirstFinish++
    }
  }

  function afterSetPagePlayed(response) {
    const { count, changed } = response.json
    if (changed && TRACK_PAGE_READ_ALOUD_COUNTS.includes(count)) {
      trackEvent('read-aloud', 'pages-read-aloud-count', count)
    }
  }

  function showPlayErrorDialog() {
    up.layer.open({
      content: `
        <h3 class="text-danger text-center">Hoppla!</h3>
        <p>Leider funktioniert das Vorlesen auf deinem Gerät nicht richtig.</p>
        <p>Bitte überprüfe deine Internetverbindung und Browser-Einstellungen.</p>
        <button class="btn btn-primary" type="button" up-dismiss>Schließen</button>
      `,
      size: 'auto',
    })
  }

  trackEvent('read-aloud', 'readable-content-present')

  up.on(element, 'click', startButtonSelector, onClickButton)
  up.on(element, 'click', stopButtonSelector, onClickButton)

  audios.forEach((audio) => {
    audio.addEventListener('ended', () => {
      onAudioEnded(audio)
    })
  })

  updateVisibleButtonText()

  if (autoPlay) {
    try {
      await play({ throwError: true })
      trackEvent('read-aloud', 'auto-play')
    } catch {
      // Autoplay was prevented
      trackEvent('read-aloud', 'auto-play-prevented')
    }
  }

  return [
    up.on('app:playback:ended', onAnyPlaybackEnded),
    up.on('app:playback:revealed', onPlaybackContentRevealed),
    () => { if (currentAudio) stopAudio(currentAudio) },
  ]

})
